/**
 * Board geometry & zones (rule.md §2). Verbatim from
 * `packages/tri-anger-engine/docs/hex-board-v2.json`: a radius-3 flat-top axial
 * hex (37 cells) minus the impassable center hole `0,0` = 36 usable cells,
 * 180°-symmetric. Coords are axial `(q,r)` with `s = -q-r`; cell ids are the
 * `"q,r"` string keys used by the map file.
 */
import type { PlayerId } from "../types/common";

export type CellId = string; // "q,r"
export type CellKind = "empty" | "node" | "home";

export interface Cell {
  readonly q: number;
  readonly r: number;
  readonly id: CellId;
  readonly kind: CellKind;
  /** Set only on `home` cells. */
  readonly owner?: PlayerId;
}

export const BOARD_RADIUS = 3;
export const HOLE: CellId = "0,0";

/** The 6 flat-top axial neighbour directions (hex-board-v2.json `adjacency`). */
export const DIRECTIONS: readonly (readonly [number, number])[] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
] as const;

const toId = (q: number, r: number): CellId => `${q},${r}`;

/** Home cells per player (enemy reaching one ends the game — rule.md §4). */
export const HOME_CELLS: Readonly<Record<PlayerId, readonly CellId[]>> = {
  p1: ["-3,3", "-2,3"],
  p2: ["2,-3", "3,-3"],
} as const;

/** Energy nodes — inert terrain in the basic game (rule.md §2). */
export const NODE_CELLS: readonly CellId[] = [
  "-3,1",
  "-1,-1",
  "-1,1",
  "0,-3",
  "0,3",
  "1,-1",
  "1,1",
  "3,-1",
] as const;

/** Canonical mirror-symmetric starting deployment (rule.md §3). */
export const START_POSITIONS: Readonly<Record<PlayerId, readonly CellId[]>> = {
  p1: ["-3,2", "-2,2", "-2,1", "-1,2"],
  p2: ["3,-2", "2,-2", "2,-1", "1,-2"],
} as const;

const HOME_OWNER: Readonly<Record<CellId, PlayerId>> = {
  "-3,3": "p1",
  "-2,3": "p1",
  "2,-3": "p2",
  "3,-3": "p2",
};
const NODE_SET: ReadonlySet<CellId> = new Set(NODE_CELLS);

function buildCells(): Readonly<Record<CellId, Cell>> {
  const cells: Record<CellId, Cell> = {};
  for (let q = -BOARD_RADIUS; q <= BOARD_RADIUS; q++) {
    const rLo = Math.max(-BOARD_RADIUS, -q - BOARD_RADIUS);
    const rHi = Math.min(BOARD_RADIUS, -q + BOARD_RADIUS);
    for (let r = rLo; r <= rHi; r++) {
      const id = toId(q, r);
      if (id === HOLE) continue;
      const owner = HOME_OWNER[id];
      if (owner) {
        cells[id] = { q, r, id, kind: "home", owner };
      } else {
        cells[id] = { q, r, id, kind: NODE_SET.has(id) ? "node" : "empty" };
      }
    }
  }
  return cells;
}

/** All 36 usable cells, keyed by id. */
export const CELLS: Readonly<Record<CellId, Cell>> = buildCells();
export const CELL_IDS: readonly CellId[] = Object.keys(CELLS);

/** Is `id` a real, passable board cell (i.e. exists and is not the hole)? */
export const isCell = (id: CellId): boolean => CELLS[id] !== undefined;

/** Adjacent existing cells (the hole and off-board coords are excluded). */
export function neighbors(id: CellId): readonly CellId[] {
  const cell = CELLS[id];
  if (!cell) return [];
  const out: CellId[] = [];
  for (const [dq, dr] of DIRECTIONS) {
    const nid = toId(cell.q + dq, cell.r + dr);
    if (CELLS[nid]) out.push(nid);
  }
  return out;
}

export const areAdjacent = (a: CellId, b: CellId): boolean =>
  neighbors(a).includes(b);

/** Is `id` a home cell belonging to `owner`? */
export const isHomeOf = (id: CellId, owner: PlayerId): boolean =>
  HOME_OWNER[id] === owner;

/** BFS hex-distance between two cells over the connected board (rule.md §4 tie-break). */
export function hexDistance(a: CellId, b: CellId): number {
  if (a === b) return 0;
  const seen = new Set<CellId>([a]);
  let frontier: CellId[] = [a];
  let dist = 0;
  while (frontier.length > 0) {
    dist += 1;
    const next: CellId[] = [];
    for (const cur of frontier) {
      for (const nid of neighbors(cur)) {
        if (nid === b) return dist;
        if (!seen.has(nid)) {
          seen.add(nid);
          next.push(nid);
        }
      }
    }
    frontier = next;
  }
  return Number.POSITIVE_INFINITY;
}
